import { useCallback, useRef, useState } from 'react';
import { ConfirmModal } from '../components/ConfirmModal';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

/**
 * window.confirm()/alert() yerine kullanılan, sitenin kendi tasarımıyla
 * Promise<boolean> döndüren onay diyaloğu hook'u.
 *
 * Kullanım:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   if (!(await confirm('Bu sınıfı arşivlemek istediğine emin misin?'))) return;
 *   ...
 *   return <>{ConfirmDialog}...</>;
 */
export function useConfirm() {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions | string) => {
    const normalized: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts;
    setOptions(normalized);
    return new Promise<boolean>(resolve => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleConfirm = useCallback(() => {
    resolverRef.current?.(true);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  const handleCancel = useCallback(() => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  const ConfirmDialog = (
    <ConfirmModal
      isOpen={options !== null}
      title={options?.title ?? 'Emin misin?'}
      message={options?.message ?? ''}
      confirmLabel={options?.confirmLabel}
      cancelLabel={options?.cancelLabel}
      danger={options?.danger}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, ConfirmDialog };
}
